import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Box from '@mui/material/Box';
import { Theme, useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import { SxProps } from '@mui/system';
import React, { useState } from 'react';

import { unreadTabAccentSx } from '~components/Messaging/unreadTabAccentSx';
import TabLabelWithAdornment from '~components/Tabs/TabLabelWithAdornment';

interface TabProp {
  accentColor?: string;
  endAdornment?: React.ReactNode;
  label: string;
  id: string;
  content: React.ReactNode;
  headerClassName?: string;
  onSelected?: (tab: string) => void;
}
export interface Props {
  tabs: Array<TabProp>;
  selectedTab?: string;
  sx?: SxProps<Theme>; // Style
  tabPanelStyle?: SxProps<Theme>; // Style
  tabStyle?: SxProps<Theme>; // Style
  /**
   * Height of the panel in pixels.
   * @example 300 // 300px
   */
  panelHeight?: number;
}
const SmallTabs = ({
  tabs,
  selectedTab,
  panelHeight,
  sx = {},
  tabPanelStyle = {},
  tabStyle = {},
}: Props) => {
  const theme = useTheme();
  const [tab, setTab] = useState<string>(selectedTab || tabs[0]?.id || '');
  // A tab can drop out of the list while selected; adopting the fallback keeps it from taking the
  // panel back if it reappears.
  const isSelectedTabListed = tabs.some((candidate) => candidate.id === tab);
  const visibleTab = isSelectedTabListed ? tab : tabs[0]?.id || '';
  if (visibleTab !== tab && visibleTab) {
    setTab(visibleTab);
  }
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    const tab = tabs.find((t) => t.id === newValue);
    tab?.onSelected?.(newValue);
    setTab(newValue);
  };

  const tabHeight = '36px';

  const styles = {
    minHeight: tabHeight,
    height: tabHeight,
    root: {
      '& .MuiTab-root': {
        minWidth: 'unset',
        width: '50%',
      },
    },

    background: theme.semantic.surfaceSunken,
    border: `1px solid ${theme.palette.divider}`,
    mr: 1,
    py: 0.5,

    '&.Mui-selected': {
      // text.primary is mode-aware so the selected label contrasts with the
      // paper surface in both modes; primary.contrastText is a mode-fixed dark
      // ink that would sit dark-on-dark once background.paper darkens.
      color: theme.palette.text.primary,
      background: theme.palette.background.paper,
      borderBottomColor: 'transparent',
      '&.MuiTabs-indicator': {
        display: 'none',
      },
    },
  };

  const selectedAccentColor = tabs.find(
    (candidate) => candidate.id === visibleTab,
  )?.accentColor;

  return (
    <Box sx={sx} className={'MuiTabs__small'}>
      <TabContext value={visibleTab}>
        <TabList
          onChange={handleChange}
          sx={{
            minHeight: tabHeight,
            height: tabHeight,
            ...(selectedAccentColor
              ? { '& .MuiTabs-indicator': { backgroundColor: selectedAccentColor } }
              : {}),
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={`header_${tab.id}`}
              label={
                <TabLabelWithAdornment
                  endAdornment={tab.endAdornment}
                  label={tab.label}
                />
              }
              value={tab.id}
              className={tab.headerClassName}
              sx={{
                ...styles,
                ...tabStyle,
                ...(tab.accentColor
                  ? {
                      ...unreadTabAccentSx(tab.accentColor),
                      '&.Mui-selected': {
                        ...styles['&.Mui-selected'],
                        color: tab.accentColor,
                      },
                    }
                  : {}),
              }}
            />
          ))}
        </TabList>
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          {tabs.map((tab) => (
            <TabPanel
              key={tab.id}
              value={tab.id}
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderTop: 'none',
                borderRadius: `0 0 ${theme.spacing(0.5)} ${theme.spacing(0.5)}`,
                overflow: panelHeight ? 'auto' : 'hidden',
                backgroundColor: theme.palette.background.paper,
                height: panelHeight ? `${panelHeight}px` : 'auto',
                ...tabPanelStyle,
              }}
            >
              {tab.content}
            </TabPanel>
          ))}
        </Box>
      </TabContext>
    </Box>
  );
};

export { SmallTabs };
